import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { loadMonthlyAttendanceRows } from "../../helpers/payrollAttendanceCore";
import { loadEmployeesAcrossBranches, summarizeHeadcount } from "../../helpers/payrollReportData";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function PayrollBranchComparisonReport() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRows, ...attResults] = await Promise.all([
        loadEmployeesAcrossBranches(storedUser),
        ...branchPool.map((shop) => loadMonthlyAttendanceRows(shop, month)),
      ]);
      const head = summarizeHeadcount(empRows);
      const headByBranch = Object.fromEntries(head.map((h) => [h.branch, h]));

      const next = branchPool.map((shop, i) => {
        const att = attResults[i];
        const list = att.rows || [];
        const wd = att.workingDays?.length || 0;
        let presentSum = 0;
        for (const r of list) presentSum += r.presentDays || 0;
        const denom = Math.max(1, list.length * wd);
        const presentRatio = wd ? presentSum / denom : 0;
        const h = headByBranch[shop.label] || { working: 0, onLeave: 0, left: 0, other: 0, total: 0 };
        return {
          branch: shop.label,
          headcountTotal: h.total,
          working: h.working,
          onLeave: h.onLeave,
          left: h.left,
          attendanceRows: list.length,
          workingDaysInView: wd,
          presentDaysSum: presentSum,
          presentPctApprox: Math.round(presentRatio * 1000) / 10,
        };
      });
      setRows(next);
      toast.success("Compared branches");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [branchPool, month, storedUser]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-5xl px-4 py-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
          <h1 className="text-2xl font-bold text-slate-900">Branch comparison</h1>
          <p className="mt-1 text-sm text-slate-600">
            Headcount from employee master vs monthly punch rollup. <strong>Present % (approx.)</strong> = sum of present
            days ÷ (employees in report × expected working days to cutoff). Useful for side-by-side KPIs.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">
              {month.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load all branches"}
          </button>
        </div>

        <div className="mt-6 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-800 text-[10px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th className="px-2 py-2">Branch</th>
                <th className="px-2 py-2 text-right">HC total</th>
                <th className="px-2 py-2 text-right">Working</th>
                <th className="px-2 py-2 text-right">On leave</th>
                <th className="px-2 py-2 text-right">Left</th>
                <th className="px-2 py-2 text-right">Present % (approx.)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Click Load.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.branch} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-medium">{r.branch}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.headcountTotal}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.working}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.onLeave}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.left}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.presentPctApprox}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
